import { assertNotEmpty } from '@festina-life/core'
import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import {
  type ConfigurationTemplateFunctionValue,
  type DeclaredConfigurationTemplateFunctionValue,
  type DynamicConfigurationTemplateFunctionValue,
  type FPlusExpressionValue,
  isDeclaredConfigurationTemplateFunctionValue,
} from '../../types/templateModel'
import { useFunctionLiteralExpressionInformation } from '../fplusExpression/useFunctionLiteralExpressionInformation'
import { TemplateFieldExpression } from '../templateFieldExpression/TemplateFieldExpression'
import { functionExpressionPath } from './fieldsHelpers'

type Props = {
  data: ConfigurationTemplateFunctionValue
  isExpanded?: boolean
}

export const FunctionValue = ({ data, isExpanded }: Props) => {
  const { templateVersion } = useTemplateContext()
  assertNotEmpty(templateVersion)
  const typeInformation = useFunctionLiteralExpressionInformation(data)

  const handleCodeChanged = useCallback(
    (nextValue: FPlusExpressionValue) => {
      assertNotEmpty(data)
      return (
        data.update as (
          nextValue:
            | DeclaredConfigurationTemplateFunctionValue
            | DynamicConfigurationTemplateFunctionValue,
        ) => Promise<void>
      )({
        ...data,
        function: nextValue,
      })
    },
    [data],
  )

  return (
    <TemplateFieldExpression
      expression={data.function}
      isExpanded={!!isExpanded}
      onChange={data.isParentNotRelevant ? undefined : handleCodeChanged}
      path={functionExpressionPath(data.path)}
      fallbackCode={
        isDeclaredConfigurationTemplateFunctionValue(data)
          ? `${data.descriptor?.signature} =`
          : ''
      }
      fromOrigin={data.origin ? functionExpressionPath(data.origin) : null}
      typeInformation={typeInformation}
    />
  )
}
